import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import Layout, { Overline } from "../components/Layout";
import { Reveal, RevealLines, stagger, item } from "../components/Motion";
import { Marquee } from "../components/Marquee";
import Icon from "../components/Icons";
import {
  IMAGES, MANIFESTO, CAPABILITY_AREAS, CAPABILITY_OUTCOMES,
  FEATURED_PROGRAMS, WHY_US,
} from "../data/site";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-eucalyptus-deep">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.heroRoad} alt="Rangers on Country" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-eucalyptus-deep/90 via-charcoal/30 to-charcoal/50" />
        <div className="grain absolute inset-0 opacity-40" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative h-full mx-auto max-w-[88rem] px-6 lg:px-10 flex flex-col justify-end pb-20 lg:pb-24">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }} className="text-sandstone mb-6">
          <Overline>Indigenous Stewardship · Worldwide</Overline>
        </motion.div>
        <h1 className="font-heading text-warmwhite font-semibold tracking-tighter leading-[0.92] text-[13vw] sm:text-7xl lg:text-8xl xl:text-[7.75rem]">
          <RevealLines lines={["Building Capacity", "for Country"]} lineClassName="pb-2" start={0.35} stagger={0.14} />
        </h1>
        <div className="mt-8 flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9 }}
            className="max-w-xl text-lg md:text-xl text-warmwhite/85 leading-relaxed"
          >
            Strengthening the people, systems and partnerships that enable long-term stewardship.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9 }}
            className="flex flex-wrap gap-4"
          >
            <Link to="/contact" data-testid="hero-work-with-us" className="group inline-flex items-center gap-2 rounded-full bg-warmwhite text-eucalyptus-deep px-7 py-4 font-semibold hover:bg-sandstone transition-colors">
              Work With Us <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
            <Link to="/training" data-testid="hero-explore-programs" className="inline-flex items-center gap-2 rounded-full border border-warmwhite/40 text-warmwhite px-7 py-4 font-semibold hover:bg-warmwhite/10 transition-colors">
              Explore Programs <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

const WhySection = () => (
  <section className="relative py-24 lg:py-36 bg-warmwhite">
    <div className="mx-auto max-w-[88rem] px-6 lg:px-10">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="text-eucalyptus mb-6"><Overline>Why Ranger Base</Overline></div>
          <Reveal>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[0.95] text-charcoal">
              Around the world, Indigenous and Traditional Owner organisations are being asked to do more than ever.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-lg text-charcoal/70 leading-relaxed max-w-md">
              Many organisations hold extraordinary cultural knowledge, yet are expected to operate
              within increasingly complex environmental, regulatory and funding systems.
            </p>
            <p className="mt-5 text-lg text-charcoal/70 leading-relaxed max-w-md">
              Ranger Base exists to help build the people, systems and capability needed for
              Indigenous stewardship to flourish over generations.
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <motion.ol
            variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-10%" }}
            className="grid sm:grid-cols-2 gap-x-10"
          >
            {MANIFESTO.map((m, i) => (
              <motion.li key={m} variants={item} className="group flex items-baseline gap-5 py-5 border-b border-charcoal/10">
                <span className="font-mono text-sm text-ochre w-8 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-heading text-xl md:text-2xl font-medium tracking-tight text-charcoal group-hover:text-eucalyptus transition-colors">{m}</span>
              </motion.li>
            ))}
          </motion.ol>
        </div>
      </div>
    </div>
  </section>
);

const Mission = () => (
  <section className="relative py-28 lg:py-40 bg-eucalyptus text-warmwhite overflow-hidden">
    <div className="grain absolute inset-0 opacity-30" />
    <div className="relative mx-auto max-w-[80rem] px-6 lg:px-10 text-center">
      <div className="text-sandstone mb-8 flex justify-center"><Overline>Our Mission</Overline></div>
      <Reveal>
        <p className="font-heading text-3xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] text-balance">
          Building Indigenous stewardship institutions that <span className="text-sandstone italic">care for Country</span>,
          strengthen culture and create <span className="text-sandstone italic">opportunity</span> for future generations.
        </p>
      </Reveal>
    </div>
  </section>
);

const Framework = () => (
  <section className="relative py-24 lg:py-36 bg-warmwhite">
    <div className="mx-auto max-w-[88rem] px-6 lg:px-10">
      <div className="max-w-3xl mb-16">
        <div className="text-eucalyptus mb-6"><Overline>Our Capability Framework</Overline></div>
        <Reveal>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter text-charcoal leading-[0.95]">
            Capability that grows over generations.
          </h2>
        </Reveal>
      </div>

      {/* Journey */}
      <div className="grid md:grid-cols-3 gap-4 mb-14">
        {["Foundation", "Growing Capability", "Long-Term Stewardship"].map((s, i) => (
          <Reveal key={s} delay={i * 0.1}>
            <div className="relative h-full rounded-3xl border border-charcoal/10 bg-white p-8 hover:border-eucalyptus/40 transition-colors">
              <span className="font-mono text-sm text-ochre">Stage {i + 1}</span>
              <h3 className="mt-3 font-heading text-2xl font-semibold tracking-tight text-charcoal">{s}</h3>
              <div className="mt-5 h-1.5 w-full rounded-full bg-sand overflow-hidden">
                <motion.div initial={{ width: 0 }} whileInView={{ width: `${(i + 1) * 33}%` }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.2 }} className="h-full bg-eucalyptus rounded-full" />
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      {/* Five areas -> outcomes */}
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <p className="text-sm tracking-overline uppercase text-earth mb-5">Five capability areas</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {CAPABILITY_AREAS.map((a, i) => (
              <Reveal key={a.key} delay={i * 0.06}>
                <div className="group flex items-start gap-4 rounded-2xl bg-white border border-charcoal/10 p-5 hover:bg-eucalyptus hover:text-warmwhite transition-colors duration-300">
                  <span className="text-eucalyptus group-hover:text-sandstone text-2xl transition-colors"><Icon name={a.icon} /></span>
                  <div>
                    <h4 className="font-heading text-lg font-semibold tracking-tight">{a.title}</h4>
                    <p className="text-sm text-charcoal/60 group-hover:text-warmwhite/75 transition-colors mt-1">{a.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="rounded-3xl bg-charcoal text-warmwhite p-8 lg:p-10">
            <p className="text-sm tracking-overline uppercase text-sandstone/70 mb-6">These combine to create</p>
            <ul className="space-y-4">
              {CAPABILITY_OUTCOMES.map((o, i) => (
                <motion.li
                  key={o} initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.6 }}
                  className="flex items-center gap-4 font-heading text-2xl md:text-3xl font-light tracking-tight"
                >
                  <span className="h-2 w-2 rounded-full bg-ochre" /> {o}
                </motion.li>
              ))}
            </ul>
            <Link to="/capability-framework" data-testid="framework-cta" className="mt-8 inline-flex items-center gap-2 text-sandstone hover:text-warmwhite transition-colors font-semibold">
              See the full framework <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Programs = () => (
  <section className="relative py-24 lg:py-36 bg-sand">
    <div className="mx-auto max-w-[88rem] px-6 lg:px-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
        <div className="max-w-2xl">
          <div className="text-eucalyptus mb-6"><Overline>Featured Programs</Overline></div>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter text-charcoal leading-[0.95]">
            Practical work, built on Country.
          </h2>
        </div>
        <Link to="/training" data-testid="programs-view-all" className="inline-flex items-center gap-2 rounded-full border border-charcoal/20 px-6 py-3 font-semibold text-charcoal hover:bg-charcoal hover:text-warmwhite transition-colors shrink-0">
          View all training <ArrowRight size={16} />
        </Link>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {FEATURED_PROGRAMS.map((p, i) => (
          <Reveal key={p.title} delay={(i % 3) * 0.08}>
            <div data-testid={`program-card-${i}`} className="group relative h-80 rounded-3xl overflow-hidden bg-charcoal cursor-default">
              <img src={p.image} alt={p.title} className="absolute inset-0 h-full w-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-[transform,opacity] duration-700 ease-out" />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/30 to-transparent" />
              <div className="relative h-full p-6 flex flex-col justify-between text-warmwhite">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-warmwhite/15 backdrop-blur-sm text-xl">
                  <Icon name={p.icon} />
                </span>
                <div>
                  <h3 className="font-heading text-2xl font-semibold tracking-tight">{p.title}</h3>
                  <p className="mt-2 text-sm text-warmwhite/80 leading-relaxed max-h-0 opacity-0 group-hover:max-h-24 group-hover:opacity-100 transition-[max-height,opacity] duration-500 overflow-hidden">{p.blurb}</p>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const MarqueeBand = () => (
  <section className="py-16 lg:py-20 bg-warmwhite border-y border-charcoal/10 text-charcoal">
    <Marquee items={["Caring for Country", "Building capability", "Walking together", "Future generations", "Indigenous knowledge", "Adaptive management"]} />
  </section>
);

const WhyUs = () => (
  <section className="relative py-24 lg:py-36 bg-warmwhite">
    <div className="mx-auto max-w-[88rem] px-6 lg:px-10">
      <div className="max-w-3xl mb-16">
        <div className="text-eucalyptus mb-6"><Overline>Why Organisations Work With Us</Overline></div>
        <Reveal>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter text-charcoal leading-[0.95]">
            We measure success by what remains after we leave.
          </h2>
        </Reveal>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-charcoal/10 rounded-3xl overflow-hidden border border-charcoal/10">
        {WHY_US.map((w, i) => (
          <Reveal key={w.title} delay={(i % 3) * 0.06}>
            <div className="group h-full bg-warmwhite p-8 lg:p-10 hover:bg-eucalyptus transition-colors duration-300">
              <span className="text-eucalyptus group-hover:text-sandstone text-3xl transition-colors"><Icon name={w.icon} /></span>
              <h3 className="mt-6 font-heading text-xl font-semibold tracking-tight text-charcoal group-hover:text-warmwhite transition-colors">{w.title}</h3>
              <p className="mt-3 text-charcoal/60 group-hover:text-warmwhite/80 transition-colors leading-relaxed">{w.desc}</p>
            </div>
          </Reveal>
        ))}
        <Reveal delay={0.12}>
          <div className="h-full bg-charcoal p-8 lg:p-10 flex flex-col justify-between">
            <p className="font-heading text-2xl font-light tracking-tight text-warmwhite leading-snug">Ready to build a program that lasts?</p>
            <Link to="/contact" data-testid="whyus-cta" className="mt-6 inline-flex items-center gap-2 rounded-full bg-ochre text-white px-6 py-3 font-semibold hover:bg-ochre/90 transition-colors w-fit">
              Start a conversation <ArrowUpRight size={16} />
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <Layout
      title="Guardians of a Changing Country"
      description="Ranger Base partners with Indigenous and Traditional Owner organisations worldwide to build strong, self-sustaining ranger programs — Indigenous ranger training, carbon assessments, biodiversity monitoring, native seed and protected-area planning around the world."
    >
      <Hero />
      <WhySection />
      <Mission />
      <Framework />
      <Programs />
      <MarqueeBand />
      <WhyUs />
    </Layout>
  );
}
